/**
 * PURPOSE: Listens for specific actions and determines when a route change should be performed.
 */

// externals
import { push, replace } from 'connected-react-router';

// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { NavigationActions } from '@makemydeal/dr-activities-common';
import type { ReduxMiddleware, ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';
import type { SalesCheckinRootState } from '@makemydeal/dr-sales-checkin-store';
import {
    SET_WORKFLOW_STATUS,
    activateSection,
    canNavigateOnWorkflowChange,
    getWorkflowRouteMap,
    isFullPageScrollEnabled,
    sectionKeyForWorkflowStatus,
    setWorkflowEvaluation,
    shouldReplaceHistory
} from '@makemydeal/dr-sales-checkin-store';
import { appendQueryString, redirectToSalesView } from '@makemydeal/dr-sales-checkin-ui-utils';
import { WorkflowStatus } from '@makemydeal/dr-sales-checkin-types';

// interfaces/types

// Dev-only params that must not be forwarded in production.
// TODO: [Revisit] Centralise this list and the filtering logic into a shared utility
// once the query-param strategy is fully settled across SCI.

export const navigationMiddleware: ReduxMiddleware<SalesCheckinRootState> =
    (store: ReduxStore<SalesCheckinRootState>) => (next: ReduxNext) => (action: AnyFSA) => {
        if (action && action.type) {
            // Capture the workflow status BEFORE the reducer runs so we can detect
            // transitions away from the Deal Setup (INVALID) and replace history instead
            // of pushing, keeping the Deal Setup out of the browser history stack.
            const prevStatus = store.getState().salesCheckin?.workflow?.status;
            switch (action.type) {
                case NavigationActions.NAVIGATE_TO: {
                    const routeIntent = action.payload;
                    // When we're already on the target route, drop NAVIGATE_TO before next() so the reducers
                    // listening to it don't reset state and trigger an unnecessary re-render.
                    // The `router` slice is injected by connected-react-router and isn't part of SalesCheckinRootState.
                    const routerState = (store.getState() as { router?: { location?: { pathname?: string } } }).router;
                    const currentPathname = routerState?.location?.pathname ?? '';
                    if (currentPathname === routeIntent) {
                        return;
                    }
                    // Append session query params so URL identity is preserved across route changes.
                    store.dispatch(push(appendQueryString(routeIntent)));
                    break;
                }

                case SET_WORKFLOW_STATUS: {
                    if (action.payload === WorkflowStatus.REDIRECT_TO_SALES_VIEW) {
                        redirectToSalesView();
                        break;
                    }

                    const workflowState = store.getState();
                    // A trade-in save that leaves the deal missing a vehicle keeps the user on the
                    // trade-in screen instead of redirecting to inventory (see S3 in
                    // docs/feat/US2083714-update-workflow-based-on-quick-quote).
                    if (!canNavigateOnWorkflowChange(workflowState)) {
                        break;
                    }
                    const status = action.payload as WorkflowStatus;
                    const sectionKey = sectionKeyForWorkflowStatus(status);
                    if (isFullPageScrollEnabled(workflowState) && sectionKey) {
                        store.dispatch(activateSection(sectionKey));
                    }
                    store.dispatch(setWorkflowEvaluation(false));

                    // Under the full-scroll toggle this map sends every section status to the
                    // one-page address, so the push lands on the page and the section the status
                    // names is reached by scroll position rather than by URL.
                    const route = getWorkflowRouteMap(workflowState)[status];
                    // Safety guard: empty route means no push should fire.
                    // Covers IDLE (bootstrap in flight) and REDIRECT_TO_SALES_VIEW (handled above).
                    if (!route) break;

                    const workflowRouterState = (workflowState as { router?: { location?: { pathname?: string } } }).router;
                    const currentPathname = workflowRouterState?.location?.pathname;

                    // Already there — a second push would duplicate the history entry and make Back
                    // look broken. Happens when an explicit NAVIGATE_TO beat us to the same route.
                    // `break`, not `return`: the reducer must still record the new status.
                    if (currentPathname === route) break;

                    // The Deal Setup (INVALID) is a transient gate — replace rather than push so it
                    // stays out of the history stack, both arriving and leaving.
                    const navigate = shouldReplaceHistory(prevStatus) ? replace : push;

                    // Append session query params so URL identity survives the route change.
                    store.dispatch(navigate(appendQueryString(route)));
                    break;
                }

                default: {
                    break;
                }
            }
        }

        next(action);
    };
